import React, { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { userAPI } from '@/services/api';
import { motion } from 'framer-motion';
import { 
  User,
  Mail,
  Phone,
  Globe,
  Calendar,
  Shield,
  Lock,
  Eye,
  EyeOff,
  X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.3 }
};

export default function ProfilePage() {
  const { user } = useAuth();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const handleChangePassword = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordData.new_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
      await userAPI.changePassword({
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      toast.success('Password changed successfully');
      setDialogOpen(false);
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const tierColors = {
    Basic: 'bg-white/10 text-white/70',
    Premium: 'bg-cyan-500/20 text-cyan-400',
    VIP: 'bg-yellow-500/20 text-yellow-400'
  };

  return (
    <motion.div 
      initial="initial"
      animate="animate"
      className="space-y-4 sm:space-y-6"
      data-testid="profile-page"
    >
      {/* Header */}
      <motion.div variants={fadeInUp}>
        <h1 className="text-2xl sm:text-3xl font-outfit font-bold text-white">Profile</h1>
        <p className="text-white/60 mt-1 text-sm sm:text-base">Manage your account settings</p>
      </motion.div>

      {/* Profile Card */}
      <motion.div variants={fadeInUp} className="glass-card p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
          <div className="w-16 h-16 sm:w-24 sm:h-24 rounded-full bg-gradient-to-br from-cyan-500/20 to-emerald-500/20 flex items-center justify-center shrink-0">
            <User className="w-8 h-8 sm:w-12 sm:h-12 text-cyan-400" />
          </div>
          <div className="text-center sm:text-left">
            <h2 className="text-xl sm:text-2xl font-outfit font-bold text-white">{user?.name}</h2>
            <p className="text-white/60 text-sm sm:text-base">{user?.email}</p>
            <div className="flex items-center gap-2 mt-2 justify-center sm:justify-start flex-wrap">
              <span className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${tierColors[user?.tier] || tierColors.Basic}`}>
                {user?.tier || 'Basic'} Tier
              </span>
              <span className="status-badge status-badge-success">
                {user?.status || 'Active'}
              </span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Info Grid */}
      <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
        {/* Account Information */}
        <motion.div variants={fadeInUp} className="glass-card p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-outfit font-semibold text-white mb-3 sm:mb-4 flex items-center gap-2">
            <User className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-400" />
            Account Information
          </h3>
          <div className="space-y-3 sm:space-y-4">
            <div className="flex items-center gap-3 p-2 sm:p-3 rounded-lg bg-white/5">
              <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-white/50 shrink-0" />
              <div className="min-w-0">
                <p className="text-[10px] sm:text-xs text-white/50">Email</p>
                <p className="text-white text-sm truncate">{user?.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-2 sm:p-3 rounded-lg bg-white/5">
              <Phone className="w-4 h-4 sm:w-5 sm:h-5 text-white/50 shrink-0" />
              <div className="min-w-0">
                <p className="text-[10px] sm:text-xs text-white/50">Phone</p>
                <p className="text-white text-sm truncate">{user?.phone || 'Not provided'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-2 sm:p-3 rounded-lg bg-white/5">
              <Globe className="w-4 h-4 sm:w-5 sm:h-5 text-white/50 shrink-0" />
              <div className="min-w-0">
                <p className="text-[10px] sm:text-xs text-white/50">Country</p>
                <p className="text-white text-sm">{user?.country || 'Not provided'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-2 sm:p-3 rounded-lg bg-white/5">
              <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-white/50 shrink-0" />
              <div className="min-w-0">
                <p className="text-[10px] sm:text-xs text-white/50">Member Since</p>
                <p className="text-white text-sm">
                  {user?.created_at ? format(new Date(user.created_at), 'MMMM dd, yyyy') : 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Financial Summary */}
        <motion.div variants={fadeInUp} className="glass-card p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-outfit font-semibold text-white mb-3 sm:mb-4 flex items-center gap-2">
            <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-400" />
            Financial Summary
          </h3>
          <div className="space-y-3 sm:space-y-4">
            <div className="p-3 sm:p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
              <p className="text-[10px] sm:text-xs text-emerald-400 mb-1">Available Balance</p>
              <p className="text-lg sm:text-2xl font-mono font-bold text-white">
                ${user?.balance?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}
              </p>
            </div>
            <div className="p-3 sm:p-4 rounded-lg bg-cyan-500/10 border border-cyan-500/30">
              <p className="text-[10px] sm:text-xs text-cyan-400 mb-1">Active Trading</p>
              <p className="text-lg sm:text-2xl font-mono font-bold text-white">
                ${user?.invested_amount?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}
              </p>
            </div>
            <div className="p-3 sm:p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
              <p className="text-[10px] sm:text-xs text-yellow-400 mb-1">Total Profit Earned</p>
              <p className="text-lg sm:text-2xl font-mono font-bold text-emerald-400">
                +${user?.total_profit?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Security */}
      <motion.div variants={fadeInUp} className="glass-card p-4 sm:p-6">
        <h3 className="text-base sm:text-lg font-outfit font-semibold text-white mb-3 sm:mb-4 flex items-center gap-2">
          <Lock className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-400" />
          Security
        </h3>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-white text-sm sm:text-base">Password</p>
            <p className="text-white/50 text-xs sm:text-sm">Last changed: Never</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="btn-secondary w-full sm:w-auto" data-testid="change-password-btn">
                Change Password
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0a0f1a] border-white/10 max-w-md mx-4 sm:mx-auto">
              <DialogClose className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground z-10">
                <X className="h-4 w-4 text-white/70 hover:text-white" />
                <span className="sr-only">Close</span>
              </DialogClose>
              <DialogHeader>
                <DialogTitle className="text-white text-lg">Change Password</DialogTitle>
                <DialogDescription className="text-white/60 text-sm">
                  Enter your current password and choose a new one
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-white/80 text-sm">Current Password</Label>
                  <div className="relative">
                    <Input
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={passwordData.current_password}
                      onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                      className="input-field pr-10"
                      data-testid="current-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition-colors"
                    >
                      {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-white/80 text-sm">New Password</Label>
                  <div className="relative">
                    <Input
                      type={showNewPassword ? 'text' : 'password'}
                      value={passwordData.new_password}
                      onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                      className="input-field pr-10"
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition-colors"
                    >
                      {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-white/80 text-sm">Confirm New Password</Label>
                  <Input
                    type="password"
                    value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                    className="input-field"
                    data-testid="confirm-new-password-input"
                  />
                </div>
                <Button
                  type="button"
                  onClick={handleChangePassword}
                  className="btn-primary w-full"
                  disabled={loading}
                  data-testid="save-password-btn"
                >
                  {loading ? 'Changing...' : 'Change Password'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </motion.div>
    </motion.div>
  );
}
